import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney, formatNumber } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asDateTime, asLabel, asCheck, column, createdByField, field, lastModifiedByField, lookup, type Row, related, show, text } from './kit';

const def = OBJECT_DEFS.quotes;
const money = show(formatMoney);

/** A quote: a priced offer to an account; options are sibling quotes. Rationale: quotes.md. */
export const quotes: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Quote',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('quote_number', 'Quote Number'),
    field('name', 'Name'),
    field('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
    field('opportunity_name', 'Opportunity', { link: lookup('opportunities', 'opportunity_id') }),
    field('status', 'Status', { format: asLabel }),
    field('kind', 'Type', { format: asLabel }),
    field('is_primary', 'Primary', { format: asCheck }),
    field('owner_name', 'Owner', { link: lookup('people', 'owner_id') }),
    field('one_time_list', 'One-Time List', { format: money }),
    field('one_time_net', 'One-Time Net', { format: money }),
    field('monthly_list', 'Monthly List', { format: money }),
    field('monthly_net', 'Monthly Net', { format: money }),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['quote_number', 'name', 'account_name', 'opportunity_name', 'status', 'kind', 'is_primary', 'owner_name'] },
    { key: 'pricing', label: 'Pricing', fields: ['one_time_list', 'one_time_net', 'monthly_list', 'monthly_net'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    related('lines', 'Lines', undefined, [
      column('name', 'Item', { primary: true }),
      column('product_code', 'Code'),
      column('quantity', 'Qty', { format: show((v) => formatNumber(v)), align: 'right' }),
      column('list_unit_price', 'List', { format: money, align: 'right' }),
      column('inclusion', 'Included', { format: asLabel }),
    ]),
    related('deliverables', 'Deliverables', 'deliverables', [
      column('name', 'Deliverable', { primary: true }),
      column('status', 'Status', { format: asLabel }),
      { ...column('is_exemplar', 'Exemplar'), render: (row) => asCheck(row.is_exemplar) },
      column('updated_at', 'Last Modified', { format: asDateTime }),
    ]),
  ],
  // Details, then every related list under Related; the workspace tabs (Scope …
  // Proposal) follow, added by the record view.
  tabs: [
    { key: 'details', label: 'Details', details: true },
    { key: 'related', label: 'Related', related: ['lines', 'deliverables'] },
  ],
  titleOf: (row) => text(row.quote_number),
  searchKeys: ['quote_number', 'name', 'account_name', 'opportunity_name'],
};
